import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { PostRole } from '../../../shared/interfaces/role.interface';

interface Permission {
  write: boolean;
  delete: boolean;
}

interface SubsectionPermission {
  identifier: string;
  name: string;
  permissions: Permission;
}

interface FunctionalityPermission {
  functionality: string;
  name: string;
  permissions: Permission;
  subsections?: SubsectionPermission[];
}

// Role update now also uses PostRole shape (with PostPermission entries)

@Component({
  selector: 'app-policy-form',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    MatDatepickerModule,
    FormsModule,
    MatTimepickerModule,
    RouterModule,
    CommonModule,
    LoaderComponent,
    QuillModule,
  ],
  templateUrl: './policy-form.component.html',
  styleUrls: [
    './policy-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class PolicyFormComponent implements OnDestroy {
  // Mode management
  isEditMode: boolean = false;
  policyId: number | null = null;

  // Common properties
  private _snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();
  modules: any;
  myroles: any = {};

  // Form and data properties
  policyForm: FormGroup;

  // Functionality and permissions structure based on menu configuration
  functionalityStructure: any[] = [
    {
      title: 'Usuarios',
      identifier: 'users',
      subsections: [
        { title: 'Políticas', identifier: 'policies' },
        { title: 'Usuarios', identifier: 'users_list' },
      ],
    },
    {
      title: 'Cultura empresarial',
      identifier: 'company_culture',
      subsections: [
        { title: 'Historias', identifier: 'stories' },
        { title: 'Misión y visión', identifier: 'mission_vision' },
        { title: 'Organigrama', identifier: 'organizational_chart' },
      ],
    },
    {
      title: 'Comunicación',
      identifier: 'communication',
      subsections: [
        { title: 'Buzón', identifier: 'inbox' },
        { title: 'Incorporaciones', identifier: 'incorporations' },
        { title: 'Actividades', identifier: 'activities' },
        { title: 'Noticias', identifier: 'news' },
        { title: 'Comentarios', identifier: 'comments' },
      ],
    },
    {
      title: 'Comedor',
      identifier: 'dining_room',
      subsections: [
        { title: 'General', identifier: 'general' },
        { title: 'Precios', identifier: 'prices' },
        { title: 'Pantalla de cocina', identifier: 'kitchen_screen' },
        { title: 'Pantalla de desyaunos', identifier: 'kitchenbreakfast_screen' },
      ],
    },
    {
      title: 'Nóminas',
      identifier: 'payrolls',
      subsections: [],
    },
    {
      title: 'Vacaciones',
      identifier: 'vacations',
      subsections: [
        { title: 'Tetris', identifier: 'drivers' },
        { title: 'Vacaciones chóferes', identifier: 'management' },
        { title: 'Protocolos', identifier: 'protocols' },
        { title: 'Períodos de prioridad', identifier: 'priority_periods' },
      ],
    },
    {
      title: 'Utilidades',
      identifier: 'utilities',
      subsections: [],
    },
  ];

  // For displaying permissions in table - flattened structure
  permissionsData: any[] = [];

  // Table properties
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  displayedColumns: string[] = ['functionality', 'write', 'delete'];

  private _liveAnnouncer = inject(LiveAnnouncer);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private roleService: RolesService,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private quillConfigService: QuillConfigService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();
    this.policyForm = this.fb.group({
      policyName: ['', [Validators.required]],
      policyDescription: ['', [Validators.required]],
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  openSnackBar(message: string) {
    const action = 'Cerrar';
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnInit() {
    // Check if we're in edit mode based on route params
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.policyId = +params['id'];
        this.loadPolicyData();
      } else {
        this.isEditMode = false;
        this.policyId = null;
        this.initializeAddMode();
      }
    });

    this.loadRoles();
  }

  private initializeAddMode(): void {
    // Initialize permissions for each functionality and subsection in add mode
    this.permissionsData = [];

    this.functionalityStructure.forEach((functionality) => {
      const hasSubsections =
        Array.isArray(functionality.subsections) &&
        functionality.subsections.length > 0;

      // Always push the functionality row first
      this.permissionsData.push({
        type: 'functionality',
        title: functionality.title,
        identifier: functionality.identifier,
        permissions: { write: false, delete: false },
        isParent: hasSubsections,
      });

      if (hasSubsections) {
        functionality.subsections.forEach((subsection: any) => {
          this.permissionsData.push({
            type: 'subsection',
            title: subsection.title, // indentation handled in template via type
            identifier: subsection.identifier,
            parentIdentifier: functionality.identifier,
            permissions: { write: false, delete: false },
            isParent: false,
          });
        });
      }
    });

    this.dataSource.data = this.permissionsData;
  }

  private loadPolicyData(): void {
    if (!this.policyId) return;

    this.loaderService.showLoader();
    this.getRoleById(this.policyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (role: any) => {
          // Update form values with the role data
          this.policyForm.patchValue({
            policyName: role.name,
            policyDescription: role.description,
          });

          // Convert the role permissions to our display format
          this.permissionsData = [];

          // Handle different permission structures
          if (role.permissions) {
            // Check if it's the new structure with identifiers
            if (role.permissions.length > 0 && role.permissions[0].identifier) {
              this.loadNewPermissionStructure(role.permissions);
            } else {
              // Handle legacy structure
              this.loadLegacyPermissionStructure(role.permissions);
            }
          }

          this.dataSource.data = this.permissionsData;
          this.loaderService.hideLoader();
        },
        error: (error) => {
          console.error('Error fetching role:', error);
          this.loaderService.hideLoader();
          this.openSnackBar(
            'Error al cargar la política. Por favor intente de nuevo.'
          );
        },
      });
  }

  private loadNewPermissionStructure(permissions: any[]) {
    // Handle new API structure
    permissions.forEach((permission) => {
      // Find matching functionality structure
      const functionalityConfig = this.functionalityStructure.find(
        (f) => f.identifier === permission.identifier
      );
      if (functionalityConfig) {
        const hasSubsections =
          Array.isArray(functionalityConfig.subsections) &&
          functionalityConfig.subsections.length > 0;
        // Add main functionality
        const writeOption = permission.options?.find(
          (opt: any) => opt.key === 'write'
        );
        const deleteOption = permission.options?.find(
          (opt: any) => opt.key === 'delete'
        );

        this.permissionsData.push({
          type: 'functionality',
          title: functionalityConfig.title,
          identifier: permission.identifier,
          permissions: {
            write: writeOption?.value || false,
            delete: deleteOption?.value || false,
          },
          isParent: hasSubsections,
        });

        // Add subsections only if declared in structure and children present
        if (hasSubsections && permission.children) {
          permission.children.forEach((child: any) => {
            const subsectionConfig = functionalityConfig.subsections.find(
              (s: any) => s.identifier === child.identifier
            );
            if (!subsectionConfig) return; // Skip unexpected children

            const childWriteOption = child.options?.find(
              (opt: any) => opt.key === 'write'
            );
            const childDeleteOption = child.options?.find(
              (opt: any) => opt.key === 'delete'
            );

            this.permissionsData.push({
              type: 'subsection',
              title: subsectionConfig.title,
              identifier: child.identifier,
              parentIdentifier: permission.identifier,
              permissions: {
                write: childWriteOption?.value || false,
                delete: childDeleteOption?.value || false,
              },
              isParent: false,
            });
          });
        }
      }
    });
  }

  private loadLegacyPermissionStructure(permissions: any[]) {
    // Handle legacy structure - convert to new format
    permissions.forEach((permission) => {
      const functionalityConfig = this.functionalityStructure.find(
        (f) => f.title === permission.functionality
      );
      if (functionalityConfig) {
        const hasSubsections =
          Array.isArray(functionalityConfig.subsections) &&
          functionalityConfig.subsections.length > 0;
        this.permissionsData.push({
          type: 'functionality',
          title: permission.functionality,
          identifier: functionalityConfig.identifier,
          permissions: {
            write: permission.can_write || false,
            delete: permission.can_delete || false,
          },
          isParent: hasSubsections,
        });

        if (hasSubsections) {
          functionalityConfig.subsections.forEach((subsection: any) => {
            this.permissionsData.push({
              type: 'subsection',
              title: subsection.title,
              identifier: subsection.identifier,
              parentIdentifier: functionalityConfig.identifier,
              permissions: { write: false, delete: false },
              isParent: false,
            });
          });
        }
      }
    });
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'policies',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar una política'
        : 'No tienes permisos para añadir una política';

      this.rolesService.checkWritePermission(
        'users',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/users/policies',
        permissionMessage,
        'policies'
      );
    } else {
      //  console.log('No user roles found in storage');
    }
  }

  getRoleById(id: number) {
    return this.roleService.getRoleById(id);
  }

  saveChanges() {
    this.loaderService.showLoader();

    // Mark all controls as touched to trigger validation visuals
    Object.keys(this.policyForm.controls).forEach((key) => {
      const control = this.policyForm.get(key);
      control?.markAsTouched();
    });

    if (this.policyForm.invalid) {
      this.loaderService.hideLoader();
      this.openSnackBar(
        'Por favor complete el nombre y la descripción de la política'
      );
      return;
    }

    if (this.isEditMode) {
      this.updateRole();
    } else {
      this.postRole();
    }
  }

  private postRole() {
    const formValues = this.policyForm.value;

    // Build the policy data object with new structure
    const functionalityGroups = this.groupPermissionsByFunctionality();

    const policyData: PostRole = {
      name: formValues.policyName,
      description: formValues.policyDescription,
      politics: functionalityGroups.map((group) => ({
        functionality: group.title,
        permissions: group.permissions,
        subsections: group.subsections,
      })),
    };

    // console.log('Policy data sent:', policyData);

    // Send the data to the service
    this.roleService
      .postRole(policyData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loaderService.hideLoader();
          this.router.navigate(['/home/users/policies']);
          this.openSnackBar('Política creada exitosamente');
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error creating policy:', error);
          const backendErr = error?.error;

          // Duplicate name detection when backend returns a plain string
          if (
            typeof backendErr === 'string' &&
            backendErr.includes(
              'duplicate key value violates unique constraint'
            ) &&
            backendErr.includes('Key (name)=')
          ) {
            this.openSnackBar(
              'Error, el nombre de la política no está disponible'
            );
            return;
          }

          // Validation errors with structured object e.g. { politics: ["..."] }
          if (backendErr && typeof backendErr === 'object') {
            if (
              backendErr.politics &&
              Array.isArray(backendErr.politics) &&
              backendErr.politics.length > 0
            ) {
              this.openSnackBar(backendErr.politics[0]);
              return;
            }
          }

          this.openSnackBar(
            'Error al crear la política. Por favor intente de nuevo.'
          );
        },
      });
  }

  private groupPermissionsByFunctionality(): any[] {
    const groups: any[] = [];

    // Group permissions by functionality
    const functionalityMap = new Map();

    this.permissionsData.forEach((item) => {
      if (item.type === 'functionality') {
        functionalityMap.set(item.identifier, {
          title: item.title,
          permissions: item.permissions,
          subsections: [],
        });
      } else if (item.type === 'subsection') {
        const parent = functionalityMap.get(item.parentIdentifier);
        if (parent) {
          parent.subsections.push({
            identifier: item.identifier,
            permissions: item.permissions,
          });
        }
      }
    });

    return Array.from(functionalityMap.values());
  }

  private updateRole() {
    if (!this.policyId) return;

    const formValues = this.policyForm.value;

    // Transform data to the expected format for the API
    const functionalityGroups = this.groupPermissionsByFunctionality();

    const updateData: PostRole = {
      name: formValues.policyName,
      description: formValues.policyDescription,
      politics: functionalityGroups.map((group) => ({
        functionality: group.title,
        permissions: group.permissions,
        subsections: group.subsections,
      })),
    };

    // console.log('Policy update data sent:', updateData);

    this.roleService
      .putRole(this.policyId, updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loaderService.hideLoader();
          this.openSnackBar('Política actualizada exitosamente');
          this.router.navigate(['/home/users/policies']);
        },
        error: (error) => {
          console.error('Error updating role:', error);
          this.loaderService.hideLoader();
          this.openSnackBar(
            'Error al actualizar la política. Por favor intente de nuevo.'
          );
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
